import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import { TestTube, CheckCircle, XCircle, AlertCircle, Mail } from "lucide-react";

const NewsletterTest = () => {
  const [testEmail, setTestEmail] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const { toast } = useToast();

  const runDiagnostic = async () => {
    setIsTesting(true);
    setTestResults(null);
    
    try {
      // Test 1: Check if function exists and responds
      console.log('Testing newsletter function...');
      const { data, error } = await supabase.functions.invoke('newsletter_send_fixed_20251219_1800', {
        method: 'GET'
      });
      
      if (error) {
        throw new Error(`Function error: ${error.message}`);
      }
      
      setTestResults({
        functionTest: { success: true, data },
        subscriberCount: data?.active_subscribers || 0
      });
      
      toast({
        title: "Test réussi !",
        description: `Fonction opérationnelle. ${data?.active_subscribers || 0} abonnés trouvés.`,
      });
      
    } catch (error: any) {
      console.error('Diagnostic error:', error);
      setTestResults({
        functionTest: { success: false, error: error.message }
      });
      
      toast({
        title: "Erreur de diagnostic",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast({
        title: "Email requis",
        description: "Veuillez saisir une adresse email de test",
        variant: "destructive",
      });
      return;
    }

    setIsTesting(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('newsletter_send_fixed_20251219_1800', {
        body: {
          subject: "Test Newsletter - BT-Connect SARL",
          content: `Ceci est un email de test envoyé le ${new Date().toLocaleString('fr-FR')}.

Si vous recevez cet email, votre système de newsletter fonctionne correctement !

Fonctionnalités testées :
✅ Connexion à la base de données
✅ Intégration Resend
✅ Formatage HTML automatique
✅ Envoi d'emails

Cordialement,
L'équipe BT-Connect SARL

---
Téléphones :
• 026 660 45 88
• 026 662 02 50  
• 024 552 13 13
• 079 614 84 79 (Service technique)`,
          testEmail
        }
      });
      
      if (error) {
        throw error;
      }
      
      toast({
        title: "Email de test envoyé !",
        description: `Test envoyé à ${testEmail}. Vérifiez votre boîte email.`,
      });
      
      setTestResults(prev => ({
        ...prev,
        emailTest: { success: true, data }
      }));
      
    } catch (error: any) {
      console.error('Test email error:', error);
      
      setTestResults(prev => ({
        ...prev,
        emailTest: { success: false, error: error.message }
      }));
      
      toast({
        title: "Erreur d'envoi",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-8">
          
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">Test Newsletter</h1>
            <p className="text-muted-foreground">
              Diagnostiquez et testez votre système de newsletter
            </p>
          </div>

          {/* Diagnostic */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Diagnostic du système
              </CardTitle>
              <CardDescription>
                Vérifiez que tous les composants fonctionnent correctement
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                onClick={runDiagnostic}
                disabled={isTesting}
                className="w-full"
              >
                {isTesting ? 'Test en cours...' : 'Lancer le diagnostic'}
              </Button>
              
              {testResults && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Fonction Edge disponible</span>
                    {testResults.functionTest?.success ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        OK
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erreur
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <span>Base de données</span>
                    {testResults.subscriberCount !== undefined ? (
                      <Badge variant="default" className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        {testResults.subscriberCount} abonnés
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" />
                        Erreur
                      </Badge>
                    )}
                  </div>
                  
                  {testResults.emailTest && (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span>Envoi d'email</span>
                      {testResults.emailTest.success ? (
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Envoyé
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          Échec
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {testResults?.functionTest?.success === false && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-red-800">Erreur détectée</h4>
                      <p className="text-sm text-red-700 mt-1">
                        {testResults.functionTest.error}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Test Email */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Test d'envoi d'email
              </CardTitle>
              <CardDescription>
                Envoyez un email de test pour vérifier la configuration Resend
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="testEmail">Email de test</Label>
                <Input
                  id="testEmail"
                  type="email"
                  placeholder="votre@email.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
              </div>
              
              <Button 
                onClick={sendTestEmail}
                disabled={isTesting || !testEmail}
                className="w-full"
              >
                {isTesting ? 'Envoi en cours...' : 'Envoyer email de test'}
              </Button>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Instructions de dépannage</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div>
                  <h4 className="font-medium">Si le diagnostic échoue :</h4>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• Vérifiez que la clé API Resend est configurée</li>
                    <li>• Consultez les logs dans Supabase Dashboard</li>
                    <li>• Vérifiez la connexion internet</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium">Si l'email n'arrive pas :</h4>
                  <ul className="text-sm text-muted-foreground mt-1 space-y-1">
                    <li>• Vérifiez votre dossier spam/courrier indésirable</li>
                    <li>• Attendez quelques minutes (délai de livraison)</li>
                    <li>• Vérifiez que l'adresse email est correcte</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium">Configuration Resend :</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Clé API : re_fEVwgnam_9rnRzSi9GGeLt9B53EsBrNqq (intégrée)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default NewsletterTest;